import { useState } from 'react';
import { connect } from 'react-redux';
import './chartLabels.css';
import { FormControl, IconButton, MenuItem, Popover, Select, Tooltip } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SliderWithInput from '../SliderWithInput';
import { ColorResult, SketchPicker } from 'react-color';
import SwitchWithInput from '../SwitchWithInput';
import { Dispatch } from 'redux';
import { updateCalendarStyleOptions } from '../../../redux/ChartPoperties/ChartControlsActions';
import { ChartOptionsProps, ChartOptionsStateProps } from '../CommonInterfaceForChartOptions';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';

const LabelBackgroundControl = ({
  bgKey,
  bgColorKey,
  calStyle,
  propKey,
  updateCalendarStyleOptions,
  setColorPickerFor,
  setColorPopOverOpen,
  iconColor,
}: any) => {
  return (
    <div>
      <div
        className="optionDescription"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: '0.15rem',
          marginTop: '0',
          marginBottom: '0px',
          borderTop: '1px solid lightgray',
        }}
      >
        <label
          className="enableDisableLabel"
          style={{
            marginRight: '10px',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          Label Background
        </label>

        <div
          style={{
            width: '50px',
            height: '24px',
            display: 'flex',
            transform: 'translateX(63px)',
            marginBottom: '7px',
          }}
        >
          <SwitchWithInput
            isChecked={calStyle[bgKey] ? calStyle[bgKey] : false}
            onSwitch={() => updateCalendarStyleOptions(propKey, bgKey, !calStyle[bgKey])}
          />
        </div>
      </div>
      {calStyle[bgKey] && (
        <div>
          <div
            className="optionDescription"
            style={{
              marginTop: '-6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Label Background Color
            {calStyle[bgColorKey] && calStyle[bgColorKey] !== '#E9E9E9' && (
              <Tooltip
                title="Reset to default color"
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => updateCalendarStyleOptions(propKey, bgColorKey, '#E9E9E9')}
                  sx={{
                    padding: '2px',
                    marginLeft: '5px',
                    color: 'gray',
                    '&:hover': {
                      color: '#333',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <RestartAltIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 10px 8px' }}>
            <div
              style={{
                height: '1.25rem',
                width: '25%',
                backgroundColor: calStyle[bgColorKey] || 'white',
                border: '2px solid darkgray',
                cursor: 'pointer',
              }}
              onClick={() => {
                setColorPickerFor(bgColorKey);
                setColorPopOverOpen(true);
              }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

const CalendarLabels = ({
  chartControls,
  tabTileProps,
  updateCalendarStyleOptions,
  iconColor,
  backgroundColor,
}: ChartOptionsProps & {
  updateCalendarStyleOptions: (propKey: string, option: string, value: any) => void;
  iconColor?: string;
  backgroundColor?: string;
}) => {
  var propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  var calStyle = chartControls.properties[propKey].calendarStyleOptions;
  const [isColorPopoverOpen, setColorPopOverOpen] = useState<boolean>(false);
  const [colorPickerFor, setColorPickerFor] = useState<string>('');

  const allThemes = useCombinedColorSchemes();
  const currentColorScheme = chartControls.properties[propKey].colorScheme;
  const selectedTheme =
    allThemes.find((theme: any) => theme.name === currentColorScheme) || allThemes[0];
  const dynamicColor = selectedTheme.dark ? '#ffffff' : '#757474';

  return (
    <div className="optionsInfo">
      <div
        className="optionDescription"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '0px',
          paddingTop: '8px',
          paddingLeft: '0.1rem',
        }}
      >
        <label
          htmlFor="yearLabelSwitch"
          className="enableDisableLabel"
          style={{
            fontWeight: 600,
            fontSize: '13px',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          YEAR LABEL
        </label>

        <div
          style={{
            width: '50px',
            height: '24px',
            display: 'flex',
            transform: 'translateX(63px)',
            marginBottom: '9px',
          }}
        >
          <SwitchWithInput
            isChecked={calStyle.showYearLabel}
            onSwitch={() => {
              updateCalendarStyleOptions(propKey, 'showYearLabel', !calStyle.showYearLabel);
            }}
          />
        </div>
      </div>

      {calStyle.showYearLabel ? (
        <>
          <div
            className="optionDescription"
            style={{
              marginTop: '0',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Label Margin
          </div>
          <SliderWithInput
            percent={true}
            sliderValue={calStyle.yearLabelMargin}
            sliderMinMax={{ min: 0, max: 60, step: 1 }}
            changeValue={(value: number) =>
              updateCalendarStyleOptions(propKey, 'yearLabelMargin', value)
            }
          />
          <div className="optionDescription" style={{ color: iconColor ? iconColor : '#404040' }}>
            Label Position
          </div>
          <FormControl fullWidth size="small" style={{ fontSize: '12px', borderRadius: '4px' }}>
            <Select
              value={calStyle.yearLabelPosition}
              variant="outlined"
              onChange={(e) => {
                updateCalendarStyleOptions(propKey, 'yearLabelPosition', e.target.value);
              }}
              sx={{
                fontSize: '12px',
                width: '96%',
                margin: '0 auto 0.5rem 0.3rem',
                backgroundColor: 'white',
                height: '1.5rem',
                color: '#404040',
                textAlign: 'start',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2bb9bb',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root.Mui-selected': {
                      backgroundColor: 'rgba(43, 185, 187, 0.1) !important',
                    },
                    '& .MuiMenuItem-root.Mui-selected:hover': {
                      backgroundColor: 'rgba(43, 185, 187, 0.2) !important',
                    },
                  },
                },
              }}
            >
              <MenuItem value="top" sx={{ padding: '2px 10px', fontSize: '12px' }}>
                Top
              </MenuItem>
              <MenuItem value="bottom" sx={{ padding: '2px 10px', fontSize: '12px' }}>
                Bottom
              </MenuItem>
              <MenuItem value="right" sx={{ padding: '2px 10px', fontSize: '12px' }}>
                Right
              </MenuItem>
              <MenuItem value="left" sx={{ padding: '2px 10px', fontSize: '12px' }}>
                Left
              </MenuItem>
            </Select>
          </FormControl>
          <div style={{ marginBottom: '15px' }}>
            <div
              className="optionDescription"
              style={{ marginTop: '2px', color: iconColor ? iconColor : '#404040' }}
            >
              Label Font Size
            </div>
            <SliderWithInput
              percent={true}
              sliderValue={calStyle.yearLabelFontSize}
              sliderMinMax={{ min: 0, max: 60, step: 1 }}
              changeValue={(value: any) =>
                updateCalendarStyleOptions(propKey, 'yearLabelFontSize', value)
              }
            />
          </div>
          <div>
            <div
              className="optionDescription"
              style={{
                marginTop: '-6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Label Color
              {calStyle.yearLabelColor && (
                <Tooltip
                  title="Reset to default color"
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => updateCalendarStyleOptions(propKey, 'yearLabelColor', '')}
                    sx={{
                      padding: '2px',
                      marginLeft: '5px',
                      color: 'gray',
                      '&:hover': {
                        color: '#333',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <RestartAltIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 10px 8px' }}>
              <div
                style={{
                  height: '1.25rem',
                  width: '25%',
                  backgroundColor: calStyle.yearLabelColor ? calStyle.yearLabelColor : dynamicColor,
                  border: '2px solid darkgray',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setColorPickerFor('yearLabelColor');
                  setColorPopOverOpen(true);
                }}
              ></div>
            </div>
          </div>
          <LabelBackgroundControl
            bgKey="yearLabelBackground"
            bgColorKey="yearLabelBackgroundColor"
            calStyle={calStyle}
            propKey={propKey}
            updateCalendarStyleOptions={updateCalendarStyleOptions}
            setColorPickerFor={setColorPickerFor}
            setColorPopOverOpen={setColorPopOverOpen}
            iconColor={iconColor}
          />
        </>
      ) : null}
      <div
        style={{
          borderTop: '1px solid rgb(211,211,211)',
          margin: '0.5rem 1% 0.5rem',
        }}
      ></div>

      <div
        className="optionDescription"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '5px',
          paddingLeft: '0.1rem',
        }}
      >
        <label
          htmlFor="monthLabelSwitch"
          className="enableDisableLabel"
          style={{
            fontWeight: 600,
            fontSize: '13px',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          MONTH LABEL
        </label>

        <div
          style={{
            width: '50px',
            height: '24px',
            display: 'flex',
            transform: 'translateX(63px)',
            marginBottom: '9px',
          }}
        >
          <SwitchWithInput
            isChecked={calStyle.showMonthLabel}
            onSwitch={() => {
              updateCalendarStyleOptions(propKey, 'showMonthLabel', !calStyle.showMonthLabel);
            }}
          />
        </div>
      </div>

      {calStyle.showMonthLabel ? (
        <>
          <div
            className="optionDescription"
            style={{
              marginTop: '0',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Label Margin
          </div>
          <SliderWithInput
            percent={true}
            sliderValue={calStyle.monthLabelMargin}
            sliderMinMax={{ min: 0, max: 60, step: 1 }}
            changeValue={(value: any) =>
              updateCalendarStyleOptions(propKey, 'monthLabelMargin', value)
            }
          />
          <div className="optionDescription" style={{ color: iconColor ? iconColor : '#404040' }}>
            Label Position
          </div>
          <FormControl fullWidth size="small" style={{ fontSize: '12px', borderRadius: '4px' }}>
            <Select
              value={calStyle.monthLabelPosition}
              variant="outlined"
              onChange={(e) => {
                updateCalendarStyleOptions(propKey, 'monthLabelPosition', e.target.value);
              }}
              sx={{
                fontSize: '12px',
                width: '96%',
                margin: '0 auto 0.5rem 0.3rem',
                backgroundColor: 'white',
                height: '1.5rem',
                color: '#404040',
                textAlign: 'start',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2bb9bb',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root.Mui-selected': {
                      backgroundColor: 'rgba(43, 185, 187, 0.1) !important',
                    },
                    '& .MuiMenuItem-root.Mui-selected:hover': {
                      backgroundColor: 'rgba(43, 185, 187, 0.2) !important',
                    },
                  },
                },
              }}
            >
              <MenuItem value="start" sx={{ padding: '2px 10px', fontSize: '12px' }}>
                Start
              </MenuItem>
              <MenuItem value="end" sx={{ padding: '2px 10px', fontSize: '12px' }}>
                End
              </MenuItem>
            </Select>
          </FormControl>
          <div style={{ marginBottom: '15px' }}>
            <div
              className="optionDescription"
              style={{
                marginTop: '2px',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Label Font Size
            </div>
            <SliderWithInput
              percent={true}
              sliderValue={calStyle.monthLabelFontSize}
              sliderMinMax={{ min: 0, max: 60, step: 1 }}
              changeValue={(value: number) =>
                updateCalendarStyleOptions(propKey, 'monthLabelFontSize', value)
              }
            />
          </div>
          <div>
            <div
              className="optionDescription"
              style={{
                marginTop: '-6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Label Color
              {calStyle.monthLabelColor && (
                <Tooltip
                  title="Reset to default color"
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => updateCalendarStyleOptions(propKey, 'monthLabelColor', '')}
                    sx={{
                      padding: '2px',
                      marginLeft: '5px',
                      color: 'gray',
                      '&:hover': {
                        color: '#333',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <RestartAltIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 10px 8px' }}>
              <div
                style={{
                  height: '1.25rem',
                  width: '25%',
                  backgroundColor: calStyle.monthLabelColor
                    ? calStyle.monthLabelColor
                    : dynamicColor,
                  border: '2px solid darkgray',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setColorPickerFor('monthLabelColor');
                  setColorPopOverOpen(true);
                }}
              ></div>
            </div>
          </div>
          <LabelBackgroundControl
            bgKey="monthLabelBackground"
            bgColorKey="monthLabelBackgroundColor"
            calStyle={calStyle}
            propKey={propKey}
            updateCalendarStyleOptions={updateCalendarStyleOptions}
            setColorPickerFor={setColorPickerFor}
            setColorPopOverOpen={setColorPopOverOpen}
            iconColor={iconColor}
          />
        </>
      ) : null}
      <div
        style={{
          borderTop: '1px solid rgb(211,211,211)',
          margin: '0.5rem 1% 0.5rem',
        }}
      ></div>

      <div
        className="optionDescription"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '5px',
          paddingLeft: '0.1rem',
        }}
      >
        <label
          htmlFor="dayLabelSwitch"
          className="enableDisableLabel"
          style={{
            fontWeight: 600,
            fontSize: '13px',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          DAY LABEL
        </label>

        <div
          style={{
            width: '50px',
            height: '24px',
            display: 'flex',
            transform: 'translateX(63px)',
            marginBottom: '9px',
          }}
        >
          <SwitchWithInput
            isChecked={calStyle.showDayLabel}
            onSwitch={() => {
              updateCalendarStyleOptions(propKey, 'showDayLabel', !calStyle.showDayLabel);
            }}
          />
        </div>
      </div>
      {calStyle.showDayLabel ? (
        <>
          <div
            className="optionDescription"
            style={{ marginTop: '0', color: iconColor ? iconColor : '#404040' }}
          >
            Label Margin
          </div>
          <SliderWithInput
            percent={true}
            sliderValue={calStyle.dayLabelMargin}
            sliderMinMax={{ min: 0, max: 60, step: 1 }}
            changeValue={(value: number) =>
              updateCalendarStyleOptions(propKey, 'dayLabelMargin', value)
            }
          />
          <div className="optionDescription" style={{ color: iconColor ? iconColor : '#404040' }}>
            Label Position
          </div>
          <FormControl fullWidth size="small" style={{ fontSize: '12px', borderRadius: '4px' }}>
            <Select
              value={calStyle.dayLabelPosition}
              variant="outlined"
              onChange={(e) => {
                updateCalendarStyleOptions(propKey, 'dayLabelPosition', e.target.value);
              }}
              sx={{
                fontSize: '12px',
                width: '96%',
                margin: '0 auto 0.5rem 0.3rem',
                backgroundColor: 'white',
                height: '1.5rem',
                color: '#404040',
                textAlign: 'start',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2bb9bb',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root.Mui-selected': {
                      backgroundColor: 'rgba(43, 185, 187, 0.1) !important',
                    },
                    '& .MuiMenuItem-root.Mui-selected:hover': {
                      backgroundColor: 'rgba(43, 185, 187, 0.2) !important',
                    },
                  },
                },
              }}
            >
              <MenuItem value="start" sx={{ padding: '2px 10px', fontSize: '12px' }}>
                Start
              </MenuItem>
              <MenuItem value="end" sx={{ padding: '2px 10px', fontSize: '12px' }}>
                End
              </MenuItem>
            </Select>
          </FormControl>
          <div style={{ marginBottom: '15px' }}>
            <div
              className="optionDescription"
              style={{
                marginTop: '2px',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Label Font Size
            </div>
            <SliderWithInput
              percent={true}
              sliderValue={calStyle.dayLabelFontSize}
              sliderMinMax={{ min: 0, max: 60, step: 1 }}
              changeValue={(value) =>
                updateCalendarStyleOptions(propKey, 'dayLabelFontSize', value)
              }
            />
          </div>
          <div>
            <div
              className="optionDescription"
              style={{
                marginTop: '-6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Label Color
              {calStyle.dayLabelColor && (
                <Tooltip
                  title="Reset to default color"
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => updateCalendarStyleOptions(propKey, 'dayLabelColor', '')}
                    sx={{
                      padding: '2px',
                      marginLeft: '5px',
                      color: 'gray',
                      '&:hover': {
                        color: '#333',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <RestartAltIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 10px 8px' }}>
              <div
                style={{
                  height: '1.25rem',
                  width: '25%',
                  backgroundColor: calStyle.dayLabelColor ? calStyle.dayLabelColor : dynamicColor,
                  border: '2px solid darkgray',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setColorPickerFor('dayLabelColor');
                  setColorPopOverOpen(true);
                }}
              ></div>
            </div>
          </div>
          <LabelBackgroundControl
            bgKey="dayLabelBackground"
            bgColorKey="dayLabelBackgroundColor"
            calStyle={calStyle}
            propKey={propKey}
            updateCalendarStyleOptions={updateCalendarStyleOptions}
            setColorPickerFor={setColorPickerFor}
            setColorPopOverOpen={setColorPopOverOpen}
            iconColor={iconColor}
          />
        </>
      ) : null}
      <Popover
        open={isColorPopoverOpen}
        onClose={() => setColorPopOverOpen(false)}
        onClick={() => setColorPopOverOpen(false)}
        anchorReference="anchorPosition"
        anchorPosition={{ top: 350, left: 1300 }}
      >
        <div>
          <SketchPicker
            color={
              (calStyle as any)[colorPickerFor] ? (calStyle as any)[colorPickerFor] : dynamicColor
            }
            className="sketchPicker"
            width="16rem"
            onChangeComplete={(color: ColorResult) => {
              updateCalendarStyleOptions(propKey, colorPickerFor, color.hex);
            }}
            onChange={(color: ColorResult) =>
              updateCalendarStyleOptions(propKey, colorPickerFor, color.hex)
            }
            disableAlpha
          />
        </div>
      </Popover>
    </div>
  );
};

const mapStateToProps = (state: ChartOptionsStateProps, ownprops: any) => {
  return {
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    updateCalendarStyleOptions: (propKey: string, option: string, value: any) =>
      dispatch(updateCalendarStyleOptions(propKey, option, value)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CalendarLabels);
